import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

// ============================================================================
// SCHEMA
// ============================================================================

const propertyFormSchema = z.object({
  isResidential: z.boolean().default(true),
  propertyTypeResidential: z
    .enum([
      "house",
      "unit_apartment",
      "townhouse",
      "villa",
      "duplex",
      "vacant_land",
      "rural_residential",
      "acreage",
      "other_residential",
    ])
    .optional()
    .nullable(),
  propertyTypeCommercial: z
    .enum([
      "hotel_motel",
      "serviced_apartments",
      "pub_tavern",
      "restaurant_cafe",
      "industrial_warehouse",
      "industrial_manufacturing",
      "industrial_distribution",
      "industrial_light",
      "industrial_heavy",
      "office_cbd",
      "office_suburban",
      "office_strata",
      "office_building",
      "retail_strip",
      "retail_shopping_centre",
      "retail_bulky_goods",
      "retail_freestanding",
      "retail_kiosk",
      "medical_centre",
      "dental_practice",
      "allied_health",
      "aged_care",
      "hospital",
      "childcare_centre",
      "early_learning_centre",
      "mixed_use_retail_residential",
      "mixed_use_office_residential",
      "mixed_use_industrial_residential",
      "showroom",
      "service_station",
      "car_wash",
      "car_dealership",
      "parking_station",
      "storage_facility",
      "data_centre",
      "brewery_winery",
      "quarry_extractive",
      "farm_land",
      "rural_commercial",
      "development_site",
      "land_subdivision",
      "marina",
      "other_commercial",
    ])
    .optional()
    .nullable(),
  propertyStatus: z
    .enum(["owner_occupied", "tenanted", "vacant"])
    .optional()
    .nullable(),
  addressUnavailable: z.boolean().default(false),
  addressLine1: z.string().optional().nullable(),
  addressLine2: z.string().optional().nullable(),
  suburb: z.string().optional().nullable(),
  state: z
    .enum(["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"])
    .optional()
    .nullable(),
  postcode: z
    .string()
    .optional()
    .nullable()
    .refine(
      (val) => !val || /^\d{4}$/.test(val),
      { message: "Postcode must be exactly 4 digits" }
    ),
  // Title details
  titleReference: z.string().optional().nullable(),
  titleType: z.string().optional().nullable(),
  registeredOwnersName: z.string().optional().nullable(),
  // Land details
  landAreaSqm: z.string().optional().nullable(),
  landAreaHectares: z.string().optional().nullable(),
  zoning: z.string().optional().nullable(),
  // Building details
  buildingAreaSqm: z.string().optional().nullable(),
  yearBuilt: z.number().optional().nullable(),
  constructionType: z.string().optional().nullable(),
  numberOfBedrooms: z.number().optional().nullable(),
  numberOfBathrooms: z.number().optional().nullable(),
  numberOfCarSpaces: z.number().optional().nullable(),
  // Commercial attributes
  netLettableAreaSqm: z.string().optional().nullable(),
  passingRentAnnual: z.string().optional().nullable(),
  capitalisationRate: z.string().optional().nullable(),
});

type PropertyFormData = z.infer<typeof propertyFormSchema>;

// ============================================================================
// OPTIONS
// ============================================================================

const PROPERTY_TYPE_RESIDENTIAL_OPTIONS = [
  { value: "house", label: "House" },
  { value: "unit_apartment", label: "Unit / Apartment" },
  { value: "townhouse", label: "Townhouse" },
  { value: "villa", label: "Villa" },
  { value: "duplex", label: "Duplex" },
  { value: "vacant_land", label: "Vacant Land" },
  { value: "rural_residential", label: "Rural Residential" },
  { value: "acreage", label: "Acreage" },
  { value: "other_residential", label: "Other Residential" },
];

const PROPERTY_TYPE_COMMERCIAL_OPTIONS = [
  { value: "hotel_motel", label: "Hotel / Motel" },
  { value: "serviced_apartments", label: "Serviced Apartments" },
  { value: "pub_tavern", label: "Pub / Tavern" },
  { value: "restaurant_cafe", label: "Restaurant / Cafe" },
  { value: "industrial_warehouse", label: "Industrial - Warehouse" },
  { value: "industrial_manufacturing", label: "Industrial - Manufacturing" },
  { value: "industrial_distribution", label: "Industrial - Distribution" },
  { value: "industrial_light", label: "Industrial - Light" },
  { value: "industrial_heavy", label: "Industrial - Heavy" },
  { value: "office_cbd", label: "Office - CBD" },
  { value: "office_suburban", label: "Office - Suburban" },
  { value: "office_strata", label: "Office - Strata" },
  { value: "office_building", label: "Office Building" },
  { value: "retail_strip", label: "Retail - Strip" },
  { value: "retail_shopping_centre", label: "Retail - Shopping Centre" },
  { value: "retail_bulky_goods", label: "Retail - Bulky Goods" },
  { value: "retail_freestanding", label: "Retail - Freestanding" },
  { value: "retail_kiosk", label: "Retail - Kiosk" },
  { value: "medical_centre", label: "Medical Centre" },
  { value: "dental_practice", label: "Dental Practice" },
  { value: "allied_health", label: "Allied Health" },
  { value: "aged_care", label: "Aged Care" },
  { value: "hospital", label: "Hospital" },
  { value: "childcare_centre", label: "Childcare Centre" },
  { value: "early_learning_centre", label: "Early Learning Centre" },
  { value: "mixed_use_retail_residential", label: "Mixed Use - Retail / Residential" },
  { value: "mixed_use_office_residential", label: "Mixed Use - Office / Residential" },
  { value: "mixed_use_industrial_residential", label: "Mixed Use - Industrial / Residential" },
  { value: "showroom", label: "Showroom" },
  { value: "service_station", label: "Service Station" },
  { value: "car_wash", label: "Car Wash" },
  { value: "car_dealership", label: "Car Dealership" },
  { value: "parking_station", label: "Parking Station" },
  { value: "storage_facility", label: "Storage Facility" },
  { value: "data_centre", label: "Data Centre" },
  { value: "brewery_winery", label: "Brewery / Winery" },
  { value: "quarry_extractive", label: "Quarry / Extractive" },
  { value: "farm_land", label: "Farm Land" },
  { value: "rural_commercial", label: "Rural Commercial" },
  { value: "development_site", label: "Development Site" },
  { value: "land_subdivision", label: "Land Subdivision" },
  { value: "marina", label: "Marina" },
  { value: "other_commercial", label: "Other Commercial" },
];

const PROPERTY_STATUS_OPTIONS = [
  { value: "owner_occupied", label: "Owner Occupied" },
  { value: "tenanted", label: "Tenanted" },
  { value: "vacant", label: "Vacant" },
];

const AUSTRALIAN_STATE_OPTIONS = [
  { value: "NSW", label: "NSW" },
  { value: "VIC", label: "VIC" },
  { value: "QLD", label: "QLD" },
  { value: "SA", label: "SA" },
  { value: "WA", label: "WA" },
  { value: "TAS", label: "TAS" },
  { value: "NT", label: "NT" },
  { value: "ACT", label: "ACT" },
];

// ============================================================================
// PROPS
// ============================================================================

interface PropertyFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  existingProperty?: any; // undefined = create mode
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const defaultValues: PropertyFormData = {
  isResidential: true,
  propertyTypeResidential: null,
  propertyTypeCommercial: null,
  propertyStatus: null,
  addressUnavailable: false,
  addressLine1: "",
  addressLine2: "",
  suburb: "",
  state: null,
  postcode: "",
  titleReference: "",
  titleType: "",
  registeredOwnersName: "",
  landAreaSqm: "",
  landAreaHectares: "",
  zoning: "",
  buildingAreaSqm: "",
  yearBuilt: null,
  constructionType: "",
  numberOfBedrooms: null,
  numberOfBathrooms: null,
  numberOfCarSpaces: null,
  netLettableAreaSqm: "",
  passingRentAnnual: "",
  capitalisationRate: "",
};

// ============================================================================
// NORMALIZE helpers
// ============================================================================

function normalizeForForm(property: any): PropertyFormData {
  return {
    isResidential: property.isResidential ?? true,
    propertyTypeResidential: property.propertyTypeResidential ?? null,
    propertyTypeCommercial: property.propertyTypeCommercial ?? null,
    propertyStatus: property.propertyStatus ?? null,
    addressUnavailable: property.addressUnavailable ?? false,
    addressLine1: property.addressLine1 || "",
    addressLine2: property.addressLine2 || "",
    suburb: property.suburb || "",
    state: property.state ?? null,
    postcode: property.postcode || "",
    titleReference: property.titleReference || "",
    titleType: property.titleType || "",
    registeredOwnersName: property.registeredOwnersName || "",
    landAreaSqm: property.landAreaSqm?.toString() || "",
    landAreaHectares: property.landAreaHectares?.toString() || "",
    zoning: property.zoning || "",
    buildingAreaSqm: property.buildingAreaSqm?.toString() || "",
    yearBuilt: property.yearBuilt ?? null,
    constructionType: property.constructionType || "",
    numberOfBedrooms: property.numberOfBedrooms ?? null,
    numberOfBathrooms: property.numberOfBathrooms ?? null,
    numberOfCarSpaces: property.numberOfCarSpaces ?? null,
    netLettableAreaSqm: property.netLettableAreaSqm?.toString() || "",
    passingRentAnnual: property.passingRentAnnual?.toString() || "",
    capitalisationRate: property.capitalisationRate?.toString() || "",
  };
}

function buildPayload(data: PropertyFormData): Record<string, unknown> {
  return {
    isResidential: data.isResidential,
    propertyTypeResidential: data.isResidential
      ? (data.propertyTypeResidential || null)
      : null,
    propertyTypeCommercial: !data.isResidential
      ? (data.propertyTypeCommercial || null)
      : null,
    propertyStatus: data.propertyStatus || null,
    addressUnavailable: data.addressUnavailable,
    addressLine1: data.addressUnavailable ? null : (data.addressLine1 || null),
    addressLine2: data.addressUnavailable ? null : (data.addressLine2 || null),
    suburb: data.addressUnavailable ? null : (data.suburb || null),
    state: data.addressUnavailable ? null : (data.state || null),
    postcode: data.addressUnavailable ? null : (data.postcode || null),
    titleReference: data.titleReference || null,
    titleType: data.titleType || null,
    registeredOwnersName: data.registeredOwnersName || null,
    landAreaSqm: data.landAreaSqm || null,
    landAreaHectares: data.landAreaHectares || null,
    zoning: data.zoning || null,
    buildingAreaSqm: data.buildingAreaSqm || null,
    yearBuilt: data.yearBuilt ?? null,
    constructionType: data.constructionType || null,
    numberOfBedrooms: data.numberOfBedrooms ?? null,
    numberOfBathrooms: data.numberOfBathrooms ?? null,
    numberOfCarSpaces: data.numberOfCarSpaces ?? null,
    netLettableAreaSqm: data.isResidential ? null : (data.netLettableAreaSqm || null),
    passingRentAnnual: data.isResidential ? null : (data.passingRentAnnual || null),
    capitalisationRate: data.isResidential ? null : (data.capitalisationRate || null),
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PropertyFormModal({
  open,
  onOpenChange,
  securityId,
  existingProperty,
}: PropertyFormModalProps) {
  const isEdit = !!existingProperty;
  const { toast } = useToast();

  const form = useForm<PropertyFormData>({
    resolver: zodResolver(propertyFormSchema),
    defaultValues,
  });

  const isResidential = form.watch("isResidential");
  const addressUnavailable = form.watch("addressUnavailable");

  // Reset form when modal opens/closes or when property data changes
  useEffect(() => {
    if (open) {
      if (existingProperty) {
        form.reset(normalizeForForm(existingProperty));
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, existingProperty, form]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
    }
    onOpenChange(newOpen);
  };

  const mutation = useMutation({
    mutationFn: async (data: PropertyFormData) => {
      const payload = buildPayload(data);
      const endpoint = `/api/crm/securities/${securityId}/property`;
      const method = isEdit ? "PATCH" : "POST";

      const response = await apiRequest(method, endpoint, payload);
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || `Failed to ${isEdit ? "update" : "create"} property details`);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/crm/securities/${securityId}/property`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/crm/securities/${securityId}`],
      });
      toast({
        title: "Success",
        description: `Property details ${isEdit ? "updated" : "created"} successfully`,
      });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: PropertyFormData) => {
    mutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="property-form-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Property Details" : "Add Property Details"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the property details for this security"
              : "Record property-specific details for this security"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

            {/* Classification Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Classification
              </h3>

              {/* Residential Toggle */}
              <FormField
                control={form.control}
                name="isResidential"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-3">
                      <Switch
                        id="is-residential-switch"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-is-residential"
                      />
                      <Label htmlFor="is-residential-switch" className="cursor-pointer">
                        {field.value ? "Residential Property" : "Commercial Property"}
                      </Label>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                {/* Property Type — shown conditionally based on isResidential */}
                {isResidential ? (
                  <FormField
                    control={form.control}
                    name="propertyTypeResidential"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Property Type</FormLabel>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(value === "__none__" ? null : value)
                          }
                          value={field.value ?? "__none__"}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-property-type-residential">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="__none__">Select type (optional)</SelectItem>
                            {PROPERTY_TYPE_RESIDENTIAL_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="propertyTypeCommercial"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Property Type</FormLabel>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(value === "__none__" ? null : value)
                          }
                          value={field.value ?? "__none__"}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-property-type-commercial">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="__none__">Select type (optional)</SelectItem>
                            {PROPERTY_TYPE_COMMERCIAL_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Property Status — residential only */}
                {isResidential && (
                  <FormField
                    control={form.control}
                    name="propertyStatus"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Property Status</FormLabel>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(value === "__none__" ? null : value)
                          }
                          value={field.value ?? "__none__"}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-property-status">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="__none__">Select status (optional)</SelectItem>
                            {PROPERTY_STATUS_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>

            {/* Address Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Address
                </h3>
                <FormField
                  control={form.control}
                  name="addressUnavailable"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="address-unavailable-switch"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-address-unavailable"
                        />
                        <Label
                          htmlFor="address-unavailable-switch"
                          className="cursor-pointer text-sm text-muted-foreground"
                        >
                          Address unavailable
                        </Label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {!addressUnavailable && (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="addressLine1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 1</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Street address"
                            data-testid="input-address-line-1"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="addressLine2"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 2</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Unit, suite, level (optional)"
                            data-testid="input-address-line-2"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="suburb"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Suburb</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Suburb"
                              data-testid="input-suburb"
                              {...field}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State</FormLabel>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(value === "__none__" ? null : value)
                            }
                            value={field.value ?? "__none__"}
                          >
                            <FormControl>
                              <SelectTrigger data-testid="select-state">
                                <SelectValue placeholder="State" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__none__">State</SelectItem>
                              {AUSTRALIAN_STATE_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="postcode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Postcode</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="0000"
                              maxLength={4}
                              data-testid="input-postcode"
                              {...field}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              {addressUnavailable && (
                <p className="text-sm text-muted-foreground italic">
                  Address details are not available for this property.
                </p>
              )}
            </div>

            {/* Title Details Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Title Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="titleReference"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title Reference</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Lot 1 DP 12345"
                          data-testid="input-title-reference"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="titleType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title Type</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Torrens, Strata"
                          data-testid="input-title-type"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="col-span-2">
                  <FormField
                    control={form.control}
                    name="registeredOwnersName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Registered Owner(s) Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="As it appears on title"
                            data-testid="input-registered-owners-name"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Land Details Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Land Details
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="landAreaSqm"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Area (sqm)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-land-area-sqm"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="landAreaHectares"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Area (ha)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.0001"
                          placeholder="0.0000"
                          data-testid="input-land-area-hectares"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="zoning"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Zoning</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. R2 Low Density"
                          data-testid="input-zoning"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Building Details Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Building Details
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="buildingAreaSqm"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Building Area (sqm)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-building-area-sqm"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="yearBuilt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year Built</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="e.g. 1995"
                          data-testid="input-year-built"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value, 10) : null
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="constructionType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Construction Type</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Brick, Weatherboard"
                          data-testid="input-construction-type"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Residential-specific building fields */}
                {isResidential && (
                  <>
                    <FormField
                      control={form.control}
                      name="numberOfBedrooms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bedrooms</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              data-testid="input-bedrooms"
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value ? parseInt(e.target.value, 10) : null
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="numberOfBathrooms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bathrooms</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              data-testid="input-bathrooms"
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value ? parseInt(e.target.value, 10) : null
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="numberOfCarSpaces"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Car Spaces</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              data-testid="input-car-spaces"
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value ? parseInt(e.target.value, 10) : null
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Commercial Attributes Section (only for commercial) */}
            {!isResidential && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Commercial Attributes
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="netLettableAreaSqm"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Net Lettable Area (sqm)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            data-testid="input-net-lettable-area"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="passingRentAnnual"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Passing Rent (Annual AUD)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            data-testid="input-passing-rent-annual"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="capitalisationRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Capitalisation Rate (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            data-testid="input-capitalisation-rate"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                data-testid="button-cancel"
                onClick={() => handleOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="button-save"
                disabled={mutation.isPending}
              >
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEdit ? "Update Property Details" : "Save Property Details"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
